import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Table } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const RiskClassificationTablePage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        category: '',
        closed_units: 0,
        closed_percent: '',
        live_units: 0,
        live_percent: '',
        risk_pool_units: 0,
        risk_pool_percent: '',
        display_order: 0,
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/risk-table');
            if (response.data.success) {
                setRows(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching table data:', error);
            toast.error('Failed to load table data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row) => {
        setEditingRow(row);
        setFormData({
            category: row.category,
            closed_units: row.closed_units,
            closed_percent: row.closed_percent,
            live_units: row.live_units,
            live_percent: row.live_percent,
            risk_pool_units: row.risk_pool_units,
            risk_pool_percent: row.risk_pool_percent,
            display_order: row.display_order,
            is_active: row.is_active === 1 || row.is_active === true
        });
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingRow(null);
        setFormData({
            category: '',
            closed_units: 0,
            closed_percent: '',
            live_units: 0,
            live_percent: '',
            risk_pool_units: 0,
            risk_pool_percent: '',
            display_order: rows.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingRow) {
                await axios.put(`/api/admin/risk-table/${editingRow.id}`, formData);
                toast.success('Row updated successfully');
            } else {
                await axios.post('/api/admin/risk-table', formData);
                toast.success('Row added successfully');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving row:', error);
            toast.error('Failed to save row');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/risk-table/${deleteConfirm.id}`);
            toast.success('Row deleted successfully');
            setDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting row:', error);
            toast.error('Failed to delete row');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Risk Table Data</h1>
                    <p className="text-slate-500 mt-1">Manage the data shown in the risk classification table</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Row
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12">Loading...</div>
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <Table className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-800 mb-2">No data available</h3>
                    <p className="text-slate-500">Add rows to populate the table.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                <tr>
                                    <th className="p-4">Category</th>
                                    <th className="p-4">Closed</th>
                                    <th className="p-4">Live</th>
                                    <th className="p-4">Risk Pool</th>
                                    <th className="p-4 text-center">Status</th>
                                    <th className="p-4 text-center">Order</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4 font-medium text-slate-800">{row.category}</td>
                                        <td className="p-4">
                                            <div>{row.closed_units} <span className="text-slate-400">({row.closed_percent})</span></div>
                                        </td>
                                        <td className="p-4">
                                            <div>{row.live_units} <span className="text-slate-400">({row.live_percent})</span></div>
                                        </td>
                                        <td className="p-4">
                                            <div>{row.risk_pool_units} <span className="text-slate-400">({row.risk_pool_percent})</span></div>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {row.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center text-slate-500">{row.display_order}</td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => handleEdit(row)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDelete(row.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="px-8 py-6 border-b border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-800">{editingRow ? 'Edit Row' : 'Add New Row'}</h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Category Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. High Risk"
                                    />
                                </div>

                                {/* Closed */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Closed (Units)</label>
                                    <input
                                        type="number"
                                        value={formData.closed_units}
                                        onChange={(e) => setFormData({ ...formData, closed_units: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Closed (%)</label>
                                    <input
                                        type="text"
                                        value={formData.closed_percent}
                                        onChange={(e) => setFormData({ ...formData, closed_percent: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. 1%"
                                    />
                                </div>

                                {/* Live */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Live (Units)</label>
                                    <input
                                        type="number"
                                        value={formData.live_units}
                                        onChange={(e) => setFormData({ ...formData, live_units: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Live (%)</label>
                                    <input
                                        type="text"
                                        value={formData.live_percent}
                                        onChange={(e) => setFormData({ ...formData, live_percent: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. 3%"
                                    />
                                </div>

                                {/* Risk Pool */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Risk Pool (Units)</label>
                                    <input
                                        type="number"
                                        value={formData.risk_pool_units}
                                        onChange={(e) => setFormData({ ...formData, risk_pool_units: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Risk Pool (%)</label>
                                    <input
                                        type="text"
                                        value={formData.risk_pool_percent}
                                        onChange={(e) => setFormData({ ...formData, risk_pool_percent: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. 2%"
                                    />
                                </div>

                                {/* Checkbox for Active */}
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                                    />
                                    <label htmlFor="isActive" className="text-sm font-semibold text-slate-700">Active</label>
                                </div>

                                {/* Order */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={formData.display_order}
                                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                                >
                                    {submitting ? 'Saving...' : 'Save Row'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Row"
                message="Are you sure? This action cannot be undone."
            />
        </div>
    );
};

export default RiskClassificationTablePage;
